import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { ServerMsg } from '@ic/engine';
import { auth } from '../lib/auth';
import { getStatus, onMessage, subscribeStatus } from '../lib/socket';
import { useStore } from '../lib/store';
import { isPlainClick, navigateMorph } from '../lib/transition';
import { Icon } from './Icons';
import { OtherGames } from './OtherGames';
import { PieceIcon } from './Pieces';

const PROFILE = { to: '/profile', label: 'Profile', hint: 'History and friends', icon: <PieceIcon icon="king" color="w" set="classic" /> };
const NAV: { to: string; label: string; hint: string; icon: ReactNode }[] = [
  { to: '/play', label: 'Play', hint: 'Online, computer, local', icon: <PieceIcon icon="knight" color="w" set="classic" /> },
  { to: '/puzzles', label: 'Puzzles', hint: 'Win in one', icon: <PieceIcon icon="bomb" color="w" set="classic" /> },
  { to: '/learn', label: 'Learn', hint: 'Rules and pieces', icon: <PieceIcon icon="bishop" color="w" set="classic" /> },
  { to: '/editor', label: 'Rule Editor', hint: 'Make your own chess', icon: <PieceIcon icon="elephant" color="w" set="classic" /> },
];

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return online;
}

export function Layout() {
  const { user, admin } = useStore(auth);
  const online = useOnline();
  const ws = useSyncExternalStore(subscribeStatus, getStatus);
  const nav = useNavigate();
  const [challenge, setChallenge] = useState<Extract<ServerMsg, { t: 'challenged' }> | null>(null);
  const signedIn = !!user && !user.guest;
  const location = useLocation();

  // Stay connected on every page: the server counts visitors by their connection.
  useEffect(() => onMessage(() => {}), []);

  useEffect(() => {
    if (!signedIn) return;
    return onMessage((m) => { if (m.t === 'challenged') setChallenge(m); });
  }, [signedIn]);

  return (
    <div className="app">
      <nav className="sidebar" aria-label="Main">
        <NavLink to="/" className="logo" aria-label="IllegalChess home">
          <span className="logo-mark"><PieceIcon icon="dragon" color="w" set="classic" /></span>
          <span className="logo-text">illegal<b>chess</b></span>
        </NavLink>
        <div className="nav-group">
          {[...NAV.filter((n) => admin || n.to !== '/editor'), PROFILE].map((n) => (
            <NavLink key={n.to} to={n.to} className="nav-item" onClick={(e) => { if (isPlainClick(e)) { e.preventDefault(); navigateMorph(nav, n.to, null, 'nav'); } }}>
              {({ isActive }) => (<>
                {isActive && <span className="nav-pill" style={{ viewTransitionName: 'nav-active' } as React.CSSProperties} />}
                <span className="nav-icon">{n.icon}</span>
                <span className="nav-text">
                  <span className="nav-label">{n.label}</span>
                  <span className="nav-hint">{n.hint}</span>
                </span>
              </>)}
            </NavLink>
          ))}
        </div>
        <div className="sidebar-foot">
          {signedIn ? (
            <Link to="/profile" className="account-card">
              <span className="avatar w">{user!.name[0].toUpperCase()}</span>
              <span className="nav-text">
                <span className="nav-label">{user!.name}</span>
                <span className="nav-hint">{user!.rating ? `Rating ${user!.rating}` : 'Your profile'}</span>
              </span>
            </Link>
          ) : (
            <div className="auth-buttons">
              <Link to="/login?mode=signup" className="btn primary block">Sign up</Link>
              <Link to="/login" className="btn secondary block">Log in</Link>
            </div>
          )}
          <div className="foot-row">
            <NavLink to="/settings" className="foot-link"><Icon name="gear" size={17} /> Settings</NavLink>
            <span className="conn small" title={online ? (ws === 'online' ? 'Connected to the game server' : 'Not connected to the game server yet') : 'No internet'}>
              <span className={`dot ${online ? 'on' : ''}`} />{online ? (ws === 'online' ? 'Online' : 'Ready') : 'Offline'}
            </span>
          </div>
        </div>
      </nav>
      <main className="main">
        <div className="topbar"><OtherGames /></div>
        {!online && <div className="offline-banner">You are offline. Local games, the bot, puzzles and the Rule Editor still work.</div>}
        {challenge && (
          <div className="toast">
            <b>{challenge.from}</b> challenged you ({challenge.ruleset}).
            <button className="btn primary sm" onClick={() => { nav(`/game/${challenge.gameId}`); setChallenge(null); }}>Accept</button>
            <button className="btn ghost sm" onClick={() => setChallenge(null)}>Dismiss</button>
          </div>
        )}
        <div className="route" key={location.pathname}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
